import { MenuItem, MenuItemBase, MenuItemType, NoteMenuItem, NotesFolderMenuItem } from './menu';
import { PortalServiceMenuItemDtoPositionType } from './portal-service';

export interface NoteDto {
  globalId: string;
  parentId: string;
  createdAt: number;
  dateAdded: number;
  dateUpdated: number;
  updatedAt: number;
  type: string;
  role: string;
  title: string;
  url: string;
  locationLat: number;
  locationLng: number;
  shared: boolean;
  favorite: boolean;
  lastChangeBy: number;
  cntNotes: number;
  size: number;
  editnote: boolean;
  isEncrypted: boolean;
  isCompleted: boolean;
  workspaceId: string;
  isImported: boolean;
  isFullwidth: boolean;
  userId: number;
  isReady: boolean;
  outliner: boolean;
  readOnly?: boolean;
  childrenNotesCount?: number;
  childrenFoldersCount?: number;
  attachmentsCount?: number;
  emoji?: string;
  color?: string;
  isSharedForPortal?: boolean;
  /**
   * Use `isSharedForPortal` instead
   * @deprecated
   **/
  is_portal_share?: boolean;
  isPortalShare?: boolean;
  portalMenuItem?: NoteMenuItem | MenuItem;
  dump?: string | null;
}

export interface NotesPermissionsDto {
  notes: {
    workspaceId: string;
    userId: number;
    noteId: string;
    parentId: string;
    role: string;
    isFolder: boolean;
  }[];
  count: number;
}

export interface NoteTextDto {
  noteGlobalId: string;
  textShort: string;
  length: number;
  dateUpdated: number;
  version: number;
  nodeId: string;
}

export interface NotePreviewDto {
  noteGlobalId?: string;
  attachmentGlobalId?: string;
  createdAt?: number;
  updatedAt?: number;
}

export interface NoteAttachmentDto {
  globalId: string;
  displayName: string;
  mime: string;
  dateAdded: number;
  dateUpdated: number;
  noteGlobalId: string;
  type: string;
  role: string;
  extra: { height: number; width: number; type: string };
  isScreenshot: boolean;
  size: number;
  inList: boolean;
  storedFileUUID: string;
  isEncrypted: boolean;
  workspaceId: string;
}

export interface GetNotesResponse {
  notes: NoteDto[];
  count: number;
}

export interface NewNoteData {
  isNewRecord?: boolean;
  parentId: string;
  globalId: string;
  title: string;
  type: string;
  dateUpdated: number;
  dateAdded: number;
  createdAt?: number;
  updatedAt?: number;
  editnote?: boolean;
  isFullwidth?: boolean;
  textVersion?: number;
}

export interface NoteSearchResultItem {
  noteGlobalId: string;
  excerpt: string;
  matches: string[];
}

export interface NotesSearchResults {
  entities: NoteSearchResultItem[];
  count: number | null;
}

export interface AddNoteToPortalPayload {
  userId: number;
  portalId: string;
  orgId: string;
  workspaceId: string;
  noteId: string;
  path?: string;
  menuItem: NoteMenuItem;
  pageId?: string;
}

export interface GetPreparedPortalMenuItemPayload {
  noteId: string;
  parentId?: string;
  positionType?: PortalServiceMenuItemDtoPositionType;
  index: number;
  pageName: string;
  pageVisibility: boolean;
  existedSlugs: string[];
  hasChildren?: boolean;
  url?: string;
  pageId?: string;
  parentUrl?: string;
}

export interface AddFolderToPortalPayload {
  userId: number;
  portalId: string;
  orgId: string;
  workspaceId: string;
  noteId: string;
  path?: string;
  menuItem: NotesFolderMenuItem;
  children?: MenuItem[];
}

export interface NotesStoreNote extends NoteDto {
  inTrash?: boolean;
  isFetching?: boolean;
  showMakeInvisibleNotification?: boolean;
  isMakingInvisible?: boolean;
}

export interface TaskResponseDto {
  id: string;
  comment?: string;
  cnt?: number;
}

/**
 *
 * @export
 * @interface NoteServiceNoteExportParamsDto
 */
export interface NoteServiceNoteExportParamsDto {
  /**
   * pdf or html
   * @type {string}
   * @memberof NoteServiceNoteExportParamsDto
   */
  format: string;
  /**
   *
   * @type {string}
   * @memberof NoteServiceNoteExportParamsDto
   */
  language?: string;
  /**
   *
   * @type {number}
   * @memberof NoteServiceNoteExportParamsDto
   */
  timezone?: number;
  /**
   * Note style
   * @type {string}
   * @memberof NoteServiceNoteExportParamsDto
   */
  style?: string;
  /**
   * Note font size
   * @type {string}
   * @memberof NoteServiceNoteExportParamsDto
   */
  size?: string;
  /**
   * PDF paper format (A1, ... A5)
   * @type {string}
   * @memberof NoteServiceNoteExportParamsDto
   */
  paperFormat?: string;
  /**
   * PDF landscape
   * @type {boolean}
   * @memberof NoteServiceNoteExportParamsDto
   */
  landscape?: boolean;
  /**
   * blot id to export this single blot
   * @type {string}
   * @memberof NoteServiceNoteExportParamsDto
   */
  blotId?: string;
}

export interface GetNoteParams {
  workspaceId: string;
  noteId: string;
}

export interface GetNoteOptions {
  userId: number;
  sessionId: string | null;
  fetchDump?: boolean;
}

export interface DeleteNotePayload {
  userId: number;
  workspaceId: string;
  noteId: string;
}

export interface UpsertNoteDto {
  globalId?: string;
  userId?: number;
  parentId?: string;
  rootParentId?: string | null;
  dateAdded?: number;
  dateUpdated?: number;
  dateExpired?: number;
  type?: string;
  role?: string;
  title?: string;
  url?: string;
  locationLat?: number;
  locationLng?: number;
  shared?: boolean;
  favorite?: boolean;
  lastChangeBy?: number;
  cntNotes?: number;
  size?: number;
  editnote?: boolean;
  isEncrypted?: boolean;
  color?: string;
  isFullwidth?: boolean;
  outliner?: boolean;
  emoji?: string;
}

export interface UpsertNotePayload {
  noteServiceUrl?: string;
  userId: number;
  workspaceId: string;
  noteId: string;
  isImport?: boolean;
  isThirdPartyServiceImport?: boolean;
  textVersion?: number;
  skipNotesLimit?: boolean;
  note: UpsertNoteDto;
}

export interface UnshareNotePayload {
  userId: number;
  workspaceId: string;
  noteId: string;
}

export type NoteType = 'note' | 'folder';

export interface GetNoteRange {
  offset?: number;
  limit?: number;
}

export interface GetNoteFilter {
  type?: NoteType;
  is_portal_share?: boolean;
  globalId?: {
    $in: string[];
  };
}

export type NoteCounterType = 'todo' | 'attachment' | 'children';

export interface GetNotesPayload {
  workspaceId: string;
  userId: number;
  range?: GetNoteRange | undefined;
  filter?: GetNoteFilter | undefined;
  rootId?: string;
  /**
   * Whether to return counters for notes and folders. Like counters of childnotes, childfolders, etc.
   */
  counters?: boolean;
  count?: boolean;
  countersTypes?: NoteCounterType[];
  fetchSharedForPortal?: boolean;
  order?: string[];
}

export enum NoteItemType {
  NOTES_FOLDER = 'notesFolder',
  NOTE = 'note',
}

export interface NoteData extends MenuItemBase {
  id: string;
  icon: string;
  name: string;
  type: MenuItemType;
  updatedBy?: number;
  updatedAt: number;
  createdAt: number;
  globalId: string;
  workspaceId: string;
  userId: number;
  childrenFoldersCount?: number;
  childrenNotesCount?: number;
  shared: boolean;
}

export interface SearchNoteData extends NoteData {
  isPortalShared: boolean;
  breadcrumbs: string;
}

export interface GetNotesResult {
  items: NoteDto[];
  total: number;
}

export interface SearchNotesPayload {
  userId: number;
  workspaceId: string;
  query: string;
  buildExcerpts: boolean;
  getMatches: boolean;
  counters: boolean;
}

export interface MoveNotePayload {
  noteServiceUrl?: string;
  userId: number;
  workspaceFrom: string;
  noteFrom: string;
  workspaceId: string;
  remove: boolean;
  parentId?: string;
  titleSuffix?: string;
  duplicating?: boolean;
  taskId?: string;
  cnt?: boolean;
  notIncludePortalShare?: boolean;
}

export interface ExportNotePayload {
  userId: number;
  workspaceId: string;
  noteId: string;
  options: NoteServiceNoteExportParamsDto;
}

export interface NotesStoreNote extends NoteDto {
  inTrash?: boolean;
  isFetching?: boolean;
  showMakeInvisibleNotification?: boolean;
  isMakingInvisible?: boolean;
}

export type AddNoteEventPayload = NotesStoreNote;

export interface UpdateNoteEventPayload extends Partial<NotesStoreNote> {
  globalId: string;
  workspaceId: string;
}
